import { M365AppId } from "owa-m365-acquisitions/lib/data/M365AppId";
import type { FirstPartyAppId, LaunchPageAppId, ExternalAppId, } from "owa-m365-acquisitions/lib/data/M365AppId";
import { getAccountKind, Account } from "../utils/getAccountKind";
import { getAppBarPinnedAppIdsFromUserSettings } from "owa-m365-acquisitions/lib/pinnedApps/getAppBarPinnedAppIdsFromUserSettings";
import { readM365ApplicationsFromCache } from "../utils/readM365ApplicationsFromCache";
import { isAppBarPinnedAppsEnabled } from "owa-m365-acquisitions/lib/pinnedApps/isAppBarPinnedAppsEnabled";

import type { AppBarPinnedAppsValue } from "owa-m365-acquisitions/lib/pinnedApps/getAppBarPinnedAppIdsFromUserSettings";
import type { M365Application } from "owa-graph-schema";
import type { MailboxInfo } from "owa-client-types";
import { getIndexerValueForMailboxInfo } from "owa-client-types";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { getCurrentModule } from "owa-app-module-store";
import { logUsage } from "owa-analytics";
import { trace } from "owa-trace";
import { isCapabilityEnabled } from "owa-capabilities";
import { m365AcquisitionsCapability } from "owa-capabilities-definitions/lib/m365AcquisitionsCapability";

let lastPinSelection: string | undefined;

export const AppBarTilesIndex: {
    [key in Account]: (LaunchPageAppId | FirstPartyAppId | ExternalAppId)[];
} = {
    [Account.Business]: [
        M365AppId.Mail,
        M365AppId.Calendar,
        M365AppId.CommercialBizChat,
        M365AppId.People,
        M365AppId.Groups,
        M365AppId.Files,
        M365AppId.ToDo,
        M365AppId.ToDoMetaOs,
        M365AppId.OrgExplorer,
        M365AppId.Newsletters,
        M365AppId.VivaEngage,
        M365AppId.Bookings,
        M365AppId.OneDrive,
        M365AppId.OneDriveMetaOs,
        M365AppId.Hoop,
    ],
    [Account.Consumer]: [
        M365AppId.Mail,
        M365AppId.Calendar,
        M365AppId.ConsumerBizChat,
        M365AppId.People,
        M365AppId.Files,
        M365AppId.ToDo,
        M365AppId.ToDoMetaOs,
        M365AppId.Word,
        M365AppId.Excel,
        M365AppId.PowerPoint,
        M365AppId.OneDrive,
        M365AppId.OneDriveMetaOs,
    ],
    [Account.Edu]: [
        M365AppId.Mail,
        M365AppId.Calendar,
        M365AppId.CommercialBizChat,
        M365AppId.People,
        M365AppId.Files,
        M365AppId.ToDo,
        M365AppId.ToDoMetaOs,
        M365AppId.Word,
        M365AppId.Excel,
        M365AppId.PowerPoint,
        M365AppId.OneDrive,
        M365AppId.OneDriveMetaOs,
    ],
};

/**
 * Gets the indices of the locked and pinned apps from the cache, otherwise from user config (bootstrap scenario)
 *
 * @returns { lockedApps: string[], pinnedApps: string[] }
 */

export function getAppBarTilesIndices(mailboxInfo?: MailboxInfo): AppBarPinnedAppsValue {
    const accountKind = getAccountKind(mailboxInfo);
    const fallbackValue: AppBarPinnedAppsValue = {
        lockedApps: AppBarTilesIndex[accountKind],
        pinnedApps: [] as (LaunchPageAppId | FirstPartyAppId | ExternalAppId)[],
    };
    
    const userPins = isAppBarPinnedAppsEnabled(mailboxInfo)
        ? getAppBarPinnedAppIdsFromUserSettings(mailboxInfo)
        : undefined;
    const resolvedMailboxInfo = mailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
    const pinSource = userPins ? "UserSetting" : "Fallback";
    const fallbackReason = userPins
        ? "None"
        : isCapabilityEnabled(m365AcquisitionsCapability, mailboxInfo)
            ? "MissingOrInvalidSetting"
            : "UnsupportedMailbox";
    const module = getCurrentModule() ?? "Unknown";
    const selection = [
        getIndexerValueForMailboxInfo(resolvedMailboxInfo),
        resolvedMailboxInfo.type,
        module,
        pinSource,
        fallbackReason,
    ].join("|");
    
    if (selection !== lastPinSelection) {
        trace.info(`AppBarPinStateChanged: pinSource=${pinSource}, fallbackReason=${fallbackReason}, module=${module}, mailboxType=${resolvedMailboxInfo.type}`, "metaos");
        logUsage("AppBarPinStateChanged", resolvedMailboxInfo, {
            pinSource,
            fallbackReason,
            module,
            mailboxType: resolvedMailboxInfo.type,
        });
        lastPinSelection = selection;
    }
    
    return userPins ?? fallbackValue;
}

export function getPinnedAppInfo(index: number): {
    appId: string;
    platformType: string;
} {
    const pinnedApps: M365Application[] = getAppBarTilesOrder().pinnedApps;
    const platform: string = pinnedApps.map(app => app.platformType)[index] as string;
    const pinnedApp: string = pinnedApps[index].id;
    return { appId: pinnedApp, platformType: platform };
}

export function getAppBarTilesOrder(): {
    lockedApps: M365Application[];
    pinnedApps: M365Application[];
    flyoutApps: M365Application[];
} {
    const m365Apps = readM365ApplicationsFromCache();
    
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    const lockedApps = m365Apps?.filter(app => app.lockedIndex! > -1) ?? [];
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion.
     *	> Forbidden non-null assertion. */
    lockedApps?.sort((a, b) => a.lockedIndex! - b.lockedIndex!);
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    const pinnedApps = m365Apps?.filter(app => app.pinnedIndex! > -1) ?? [];
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion.
     *	> Forbidden non-null assertion. */
    pinnedApps?.sort((a, b) => a.pinnedIndex! - b.pinnedIndex!);
    
    const flyoutApps = m365Apps?.filter(app => app.pinnedIndex === -1 && app.isPinnable === true) ?? [];
    
    return { lockedApps, pinnedApps, flyoutApps };
}
